import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AXIS, formatHours, TOOLTIP_CURSOR, TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

export interface WaterfallStep {
  name: string;
  /** How much this step consumes. The first and last steps are absolute totals. */
  value: number;
  color: string;
  /** Absolute steps sit on the baseline; consuming steps float on the remainder. */
  absolute?: boolean;
}

interface Row {
  name: string;
  base: number;
  value: number;
  color: string;
  display: string;
}

/**
 * Waterfall: an opening total drains left-to-right into what consumed it, with
 * the remainder standing alone at the end.
 *
 * Implemented as a stacked bar with a transparent "base" series — Recharts has
 * no waterfall type, and this is the standard construction. The floating
 * position is what makes each step read as *drawn from* the total rather than
 * as an independent quantity.
 */
export function WaterfallChart({
  steps,
  height = 260,
  onStepClick,
}: {
  steps: WaterfallStep[];
  height?: number;
  onStepClick?: (name: string) => void;
}) {
  const animation = useChartAnimation();
  let running = 0;
  const rows: Row[] = steps.map((step) => {
    if (step.absolute) {
      running = step.value;
      return {
        name: step.name,
        base: 0,
        value: step.value,
        color: step.color,
        display: formatHours(step.value),
      };
    }
    const base = Math.max(0, running - step.value);
    const row: Row = {
      name: step.name,
      base,
      value: step.value,
      color: step.color,
      display: `−${formatHours(step.value)}`,
    };
    running = base;
    return row;
  });

  const ceiling = Math.max(...rows.map((r) => r.base + r.value));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 22, right: 8, bottom: 8, left: 0 }}>
          <XAxis
            dataKey="name"
            interval={0}
            height={40}
            tickLine={false}
            axisLine={{ stroke: AXIS.line }}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
          />
          <YAxis hide domain={[0, ceiling]} />
          <Tooltip
            cursor={TOOLTIP_CURSOR}
            contentStyle={TOOLTIP_STYLE}
            // The transparent base carries no meaning; only report the step.
            formatter={(value, key) =>
              toKey(key) === 'value' ? [`${formatHours(toNumber(value))} hrs`, 'hours'] : []
            }
          />
          <Bar dataKey="base" stackId="a" fill="transparent" {...animation} />
          <Bar
            dataKey="value"
            stackId="a"
            radius={[4, 4, 0, 0]}
            {...animation}
            cursor={onStepClick ? 'pointer' : undefined}
            onClick={(_, index) => {
              const row = rows[index];
              if (row) onStepClick?.(row.name);
            }}
          >
            {rows.map((r) => (
              <Cell key={r.name} fill={r.color} />
            ))}
            <LabelList
              dataKey="display"
              position="top"
              style={{ fontSize: 11, fontWeight: 700, fill: 'var(--foreground)' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
